import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import DownloadAppModal from './components/DownloadAppModal';
import { storeUrlFor } from './constants/stores';
import detectMobileOS from './utils/detectMobileOS';

const DownloadAppContext = createContext(null);

function openStoreWindow(os) {
    const href = storeUrlFor(os);
    if (href) window.open(href, '_blank', 'noopener');
}

/**
 * Owns the single "get the app" entry point every download button in the app
 * goes through (header, sidebar, home card) and mounts the QR modal once.
 */
export function DownloadAppProvider({ children }) {
    const [modalOpen, setModalOpen] = useState(false);

    const mobileOS = useMemo(() => detectMobileOS(), []);

    // A phone can't scan a code on its own screen, so it goes straight to its
    // store; a computer gets the QR code to scan with the phone.
    const open = useCallback(() => {
        if (mobileOS) openStoreWindow(mobileOS);
        else setModalOpen(true);
    }, [mobileOS]);

    const close = useCallback(() => setModalOpen(false), []);
    const openStore = useCallback((os) => openStoreWindow(os), []);

    const value = useMemo(
        () => ({ open, close, openStore, isPhone: !!mobileOS, mobileOS }),
        [open, close, openStore, mobileOS]
    );

    return (
        <DownloadAppContext.Provider value={value}>
            {children}
            <DownloadAppModal open={modalOpen} onClose={close} />
        </DownloadAppContext.Provider>
    );
}

export function useDownloadApp() {
    const context = useContext(DownloadAppContext);

    if (!context) {
        throw new Error('useDownloadApp must be used within a DownloadAppProvider.');
    }

    return context;
}
